import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import defaultTranslations from '../i18n/translations';

const AuthContext = createContext(null);
const API = process.env.REACT_APP_API_URL || '/api';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLangState] = useState('cs');
  const [customTranslations, setCustomTranslations] = useState({});

  const apiFetch = useCallback(async (path, options = {}) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error || 'Request failed');
    }
    return res.json();
  }, []);

  // Deep merge custom translations over defaults
  const mergeTranslations = (defaults, custom) => {
    const result = { ...defaults };
    for (const lang of ['cs', 'sk', 'ro']) {
      if (custom[lang]) {
        result[lang] = { ...defaults[lang], ...custom[lang] };
        // Handle nested objects like departments
        for (const key of Object.keys(custom[lang])) {
          if (typeof custom[lang][key] === 'object' && defaults[lang]?.[key]) {
            result[lang][key] = { ...defaults[lang][key], ...custom[lang][key] };
          }
        }
      }
    }
    return result;
  };

  const translations = mergeTranslations(defaultTranslations, customTranslations);

  const t = (key) => {
    const keys = key.split('.');
    let val = translations[lang];
    for (const k of keys) val = val?.[k];
    return val ?? key;
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }
    apiFetch('/auth/me')
      .then(u => {
        setUser(u);
        setLangState(u.lang || 'cs');
        // Load custom translations
        return apiFetch('/translations').then(data => {
          if (data && Object.keys(data).length > 0) setCustomTranslations(data);
        }).catch(() => {});
      })
      .catch(() => localStorage.removeItem('token'))
      .finally(() => setLoading(false));
  }, [apiFetch]);

  const login = async (email, password) => {
    const data = await apiFetch('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
    localStorage.setItem('token', data.token);
    setUser(data.user);
    setLangState(data.user.lang || 'cs');
    // Load custom translations after login
    apiFetch('/translations').then(d => { if (d && Object.keys(d).length > 0) setCustomTranslations(d); }).catch(() => {});
  };

  const logout = () => { localStorage.removeItem('token'); setUser(null); };

  const setLang = async (l) => {
    setLangState(l);
    if (user) {
      try { await apiFetch('/auth/lang', { method: 'PUT', body: JSON.stringify({ lang: l }) }); } catch {}
    }
  };

  const reloadTranslations = () => apiFetch('/translations').then(d => { if (d) setCustomTranslations(d); });

  return (
    <AuthContext.Provider value={{ user, loading, lang, t, login, logout, setLang, apiFetch, reloadTranslations }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
